import { useCallback, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import type { ActivityLog } from '../../types'
import { formatDate } from '../../utils/helpers'

export default function AdminActivityLogs() {
  const { t } = useTranslation()
  const [logs, setLogs] = useState<ActivityLog[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [filterAction, setFilterAction] = useState('')
  const limit = 25

  const loadLogs = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, unknown> = { page, limit }
      if (filterAction) params.action = filterAction
      const result = await api.getAdminActivityLogs(params)
      setLogs(result.data)
      setTotal(result.total)
    } catch {
      setLogs([])
    } finally {
      setLoading(false)
    }
  }, [page, filterAction, limit])

  useEffect(() => {
    loadLogs()
  }, [loadLogs])

  const totalPages = Math.ceil(total / limit)

  const actionColor = (action: string): string => {
    if (action.includes('error')) return 'bg-red-100 text-red-800'
    if (action.includes('login')) return 'bg-blue-100 text-blue-800'
    if (action.includes('create') || action.includes('register'))
      return 'bg-green-100 text-green-800'
    if (action.includes('update') || action.includes('edit')) return 'bg-yellow-100 text-yellow-800'
    if (action.includes('delete') || action.includes('disable')) return 'bg-red-100 text-red-800'
    return 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('admin.logs.title')}</h1>
        <span className="text-sm text-gray-500">{t('admin.logs.entries', { count: total })}</span>
      </div>

      {/* Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <label className="text-sm font-medium text-gray-700">
          {t('admin.logs.filterAction')} :
        </label>
        <input
          type="text"
          value={filterAction}
          onChange={(e) => {
            setFilterAction(e.target.value)
            setPage(1)
          }}
          placeholder="ex: login, view_invoices..."
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-64"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <>
            {/* Mobile card layout */}
            <ul className="divide-y divide-gray-100 sm:hidden">
              {logs.length === 0 ? (
                <li className="p-6 text-center text-gray-500">{t('admin.logs.none')}</li>
              ) : (
                logs.map((log) => (
                  <li key={log.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <span
                        className={`flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full ${actionColor(log.action)}`}
                      >
                        {log.action}
                      </span>
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {formatDate(log.created_at)}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {log.user_email ?? `#${log.user_id}`}
                    </p>
                    {(log.firstname || log.lastname) && (
                      <p className="text-xs text-gray-500 truncate">
                        {[log.firstname, log.lastname].filter(Boolean).join(' ')}
                      </p>
                    )}
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <span className="text-xs text-gray-500 truncate">
                        {log.entity_type
                          ? `${log.entity_type}${log.entity_id ? ` #${log.entity_id}` : ''}`
                          : ''}
                      </span>
                      <span className="text-xs text-gray-400 font-mono flex-shrink-0">
                        {log.ip_address ?? ''}
                      </span>
                    </div>
                  </li>
                ))
              )}
            </ul>

            {/* Desktop table layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                      {t('admin.logs.colDate')}
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                      {t('admin.logs.colUser')}
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                      {t('admin.logs.colAction')}
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                      {t('admin.logs.colEntity')}
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">
                      {t('admin.logs.colIp')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                        {t('admin.logs.none')}
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {formatDate(log.created_at)}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className="font-medium text-gray-900">
                            {log.user_email ?? `#${log.user_id}`}
                          </span>
                          {(log.firstname || log.lastname) && (
                            <span className="text-gray-400 ml-1">
                              ({[log.firstname, log.lastname].filter(Boolean).join(' ')})
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${actionColor(log.action)}`}
                          >
                            {log.action}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {log.entity_type
                            ? `${log.entity_type}${log.entity_id ? ` #${log.entity_id}` : ''}`
                            : t('common.none')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-400 font-mono">
                          {log.ip_address ?? t('common.none')}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Page {page} sur {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
